<template>
  <header class="px-6">
    <!-- Contexto y navegación entre lecciones -->
    <div class="flex flex-wrap items-center justify-between gap-2">
      <nav :aria-label="$t('projects.lesson_breadcrumb')" class="min-w-0 text-sm text-gray-600">
        <template v-if="position">
          <span>{{ $t('projects.section_n', { number: position.sectionNumber }) }}</span>
          <span v-if="position.section.title">: {{ position.section.title }}</span>
          <span class="mx-1.5 text-gray-400" aria-hidden="true">›</span>
          <span>{{ $t('projects.lesson_n_of_total', { number: position.lessonNumber, total: position.lessonsInSection }) }}</span>
        </template>
      </nav>
      <div v-if="position" class="flex items-center gap-2">
        <button ref="outlineToggle" type="button" v-on:click="showOutline = !showOutline"
                :aria-expanded="showOutline ? 'true' : 'false'" aria-controls="lesson-header-outline"
                class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
          <font-awesome-icon icon="list-ul" class="h-3 w-3" aria-hidden="true"/>
          {{ $t('projects.course_outline_short') }}
          <font-awesome-icon :icon="showOutline ? 'chevron-up' : 'chevron-down'" class="h-3 w-3" aria-hidden="true"/>
        </button>
        <router-link v-if="previous" :to="lessonRoute(previous)" :title="previous.title"
                     :aria-label="$t('projects.previous_lesson_named', { title: previous.title })"
                     class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
          <font-awesome-icon icon="chevron-left" class="h-3 w-3" aria-hidden="true"/>
          {{ $t('projects.previous_lesson') }}
        </router-link>
        <router-link v-if="next" :to="lessonRoute(next)" :title="next.title"
                     :aria-label="$t('projects.next_lesson_named', { title: next.title })"
                     class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
          {{ $t('projects.next_lesson') }}
          <font-awesome-icon icon="chevron-right" class="h-3 w-3" aria-hidden="true"/>
        </router-link>
      </div>
    </div>

    <!-- Índice del curso para saltar a otra lección sin volver al currículo (FE-066) -->
    <nav v-if="position && showOutline" id="lesson-header-outline" :aria-label="$t('projects.course_outline')"
         @keydown.esc.prevent="closeOutline"
         class="mt-3 max-h-80 overflow-y-auto rounded-md bg-white py-2 ring-1 ring-inset ring-gray-200">
      <ol>
        <li v-for="(section, sectionIndex) in projectsStore.project.sections" :key="section.uuid">
          <p class="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {{ $t('projects.section_n', { number: sectionIndex + 1 }) }}<span v-if="section.title">: {{ section.title }}</span>
          </p>
          <ol>
            <li v-for="(entry, lessonIndex) in section.lessons || []" :key="entry.uuid">
              <router-link :to="lessonRoute(entry)" :aria-current="entry.uuid === lesson.uuid ? 'page' : null"
                           :class="entry.uuid === lesson.uuid ? 'bg-gray-100 font-semibold text-stone-900' : 'text-gray-700 hover:bg-gray-50'"
                           class="flex items-baseline gap-2 px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-bpblue">
                <span class="w-5 shrink-0 text-right tabular-nums text-gray-400">{{ lessonIndex + 1 }}</span>
                <span class="min-w-0 flex-1 break-words">{{ entry.title }}</span>
                <span v-if="entry.uuid === lesson.uuid" class="sr-only">{{ $t('projects.current_lesson') }}</span>
                <span v-if="features.publish && !entry.published_at" class="shrink-0 text-xs text-gray-500">{{ $t('projects.status_draft') }}</span>
              </router-link>
            </li>
          </ol>
        </li>
      </ol>
    </nav>

    <!-- Título editable y estado -->
    <div class="mt-3 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div class="min-w-0 flex-1">
        <h2 v-if="!editing" class="text-xl font-bold leading-7 text-stone-900">
          <button v-if="canRename" type="button" v-on:click="startEditing" :disabled="renaming"
                  :aria-label="$t('projects.rename_lesson_named', { title: displayTitle })"
                  class="w-full break-words rounded-md px-1 -mx-1 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue disabled:opacity-60">
            {{ displayTitle }}
          </button>
          <span v-else class="break-words">{{ displayTitle }}</span>
        </h2>
        <template v-else>
          <label for="lesson-header-title" class="sr-only">{{ $t('projects.lesson_title_label') }}</label>
          <input id="lesson-header-title" ref="titleInput" v-model="title" type="text"
                 @keydown.enter.prevent="$event.target.blur()" @keydown.esc.prevent="cancelEditing" @blur="commitTitle"
                 class="block w-full rounded-md border-gray-300 px-2 py-1 text-xl font-bold text-stone-900 focus:border-bpblue focus:ring-bpblue">
        </template>
      </div>

      <div class="flex flex-wrap items-center gap-4">
        <label v-if="features.publish && storeLesson" class="flex items-center gap-2 text-sm text-gray-700">
          <toggle-component :status="isPublished" :product="storeLesson.uuid"
                            :label="$t(isPublished ? 'projects.unpublish_lesson' : 'projects.publish_lesson')"
                            :callback="(uuid, published) => setPublished(published)"/>
          <span aria-hidden="true">{{ $t(isPublished ? 'projects.status_published' : 'projects.status_draft') }}</span>
        </label>
        <label v-if="features.freePreview && storeLesson" class="flex items-center gap-2 text-sm text-gray-700">
          <toggle-component :status="!!storeLesson.is_free_preview" :product="storeLesson.uuid + '-preview'"
                            :label="$t('projects.free_preview_badge')"
                            :callback="(uuid, enabled) => setFreePreview(enabled)"/>
          <span aria-hidden="true">{{ $t('projects.free_preview_badge') }}</span>
        </label>
        <a v-if="studentViewUrl" :href="studentViewUrl" target="_blank" rel="noopener noreferrer"
           class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-bpblue ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-bpblue">
          <font-awesome-icon icon="external-link-alt" class="h-3.5 w-3.5" aria-hidden="true"/>
          {{ $t('projects.view_as_student') }}
          <span class="sr-only">{{ $t('projects.opens_in_new_tab') }}</span>
        </a>
      </div>
    </div>

    <p class="sr-only" role="status" aria-live="polite">{{ liveMessage }}</p>
  </header>
</template>

<script>
import ToggleComponent from "@/components/forms/ToggleComponent";
import NotificationService from "@/services/notification/NotificationService";
import lmsFeatures from "@/config/lmsFeatures";
import {useProjectsStore} from "@/stores/projects";
import {useAcademiesStore} from "@/stores/academies";

/*
 * Cabecera del editor de una lección (FE-053): miga «Sección › Lección»,
 * índice del curso (FE-066), anterior/siguiente, título editable, publicar, vista previa gratuita y «Ver
 * como alumno». Usa el currículo del store (el padre lo carga si hace falta);
 * hasta tenerlo, solo muestra el título.
 */
export default {
  name: "LessonHeaderComponent",
  components: {
    ToggleComponent
  },
  props: {
    lesson: {
      type: Object,
      required: true
    },
    projectUuid: {
      type: String,
      required: true
    }
  },
  setup() {
    return {
      projectsStore: useProjectsStore(),
      academiesStore: useAcademiesStore(),
    }
  },
  data() {
    return {
      features: {
        publish: lmsFeatures.publishCurriculum,
        freePreview: lmsFeatures.freePreview,
        renameLesson: lmsFeatures.renameLesson,
      },
      showOutline: false,
      editing: false,
      renaming: false,
      title: '',
      liveMessage: '',
    }
  },
  computed: {
    curriculum() {
      if (this.projectsStore.project.uuid !== this.projectUuid) return []
      return (this.projectsStore.project.sections ?? []).flatMap((section, sectionIndex) =>
          (section.lessons ?? []).map((lesson, lessonIndex) => ({section, sectionIndex, lesson, lessonIndex})))
    },
    currentIndex() {
      return this.curriculum.findIndex((entry) => entry.lesson.uuid === this.lesson.uuid)
    },
    position() {
      const entry = this.curriculum[this.currentIndex]
      if (!entry) return null
      return {
        section: entry.section,
        sectionNumber: entry.sectionIndex + 1,
        lessonNumber: entry.lessonIndex + 1,
        lessonsInSection: entry.section.lessons.length,
      }
    },
    // La lección del store: es la que actualizan las acciones de publicar y vista previa.
    storeLesson() {
      return this.curriculum[this.currentIndex]?.lesson ?? null
    },
    previous() {
      return this.currentIndex > 0 ? this.curriculum[this.currentIndex - 1].lesson : null
    },
    next() {
      return this.currentIndex >= 0 && this.currentIndex < this.curriculum.length - 1
          ? this.curriculum[this.currentIndex + 1].lesson
          : null
    },
    displayTitle() {
      return this.storeLesson?.title ?? this.lesson.title
    },
    canRename() {
      return this.features.renameLesson && !!this.position
    },
    isPublished() {
      return !!this.storeLesson?.published_at
    },
    // La lección en el visor de la web de la academia (LMS-171/225). Quien gestiona el curso ve también los borradores.
    studentViewUrl() {
      const academyUrl = this.academiesStore.academy?.url
      return academyUrl ? '//' + academyUrl + 'my-academy/courses/' + this.projectUuid + '/lessons/' + this.lesson.uuid : null
    }
  },
  watch: {
    // Al saltar a otra lección desde el índice, se cierra.
    'lesson.uuid'() {
      this.showOutline = false
    }
  },
  methods: {
    closeOutline() {
      this.showOutline = false
      this.$nextTick(() => this.$refs.outlineToggle?.focus())
    },
    lessonRoute(lesson) {
      return {name: 'projects-edit-content', params: {id: this.projectUuid, uuid: lesson.uuid}}
    },
    announce(message) {
      this.liveMessage = ''
      this.$nextTick(() => {
        this.liveMessage = message
      })
    },
    startEditing() {
      this.title = this.displayTitle
      this.editing = true
      this.$nextTick(() => this.$refs.titleInput?.focus())
    },
    cancelEditing() {
      this.editing = false
      this.focusTitle()
    },
    focusTitle() {
      this.$nextTick(() => this.$el.querySelector('h2 button')?.focus())
    },
    commitTitle() {
      if (!this.editing) return
      this.editing = false
      const title = this.title.trim()
      if (!title || title === this.displayTitle || !this.position) return

      this.renaming = true
      this.projectsStore.renameLesson(this.position.section.uuid, this.storeLesson.uuid, title)
          .then(() => {
            this.storeLesson.title = title
            this.announce(this.$t('projects.lesson_saved'))
          })
          .catch((error) => {
            NotificationService.showApiError(error, this.$t('projects.unexpected_error'))
          })
          .finally(() => {
            this.renaming = false
          })
    },
    setPublished(published) {
      this.projectsStore.setLessonPublished(this.position.section, this.storeLesson, published)
          .then(() => this.announce(this.$t(published ? 'projects.lesson_published' : 'projects.lesson_unpublished')))
          .catch((error) => NotificationService.showApiError(error, this.$t('projects.unexpected_error')))
    },
    setFreePreview(enabled) {
      this.projectsStore.setLessonFreePreview(this.position.section, this.storeLesson, enabled)
          .then(() => this.announce(this.$t(enabled ? 'projects.free_preview_enabled' : 'projects.free_preview_disabled')))
          .catch((error) => NotificationService.showApiError(error, this.$t('projects.unexpected_error')))
    }
  }
}
</script>
